import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  LogOut, 
  Wallet, 
  TrendingUp, 
  Clock, 
  CheckCircle2,
  XCircle,
  AlertCircle,
  DollarSign,
  BarChart3,
  Copy,
  Check,
  Eye,
  EyeOff,
  ArrowUpRight,
  X,
  Award,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import AchievementsSection from '../components/AchievementsSection';
import AffiliateSection from '../components/AffiliateSection';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Payout Request Modal Component
const PayoutRequestModal = ({ isOpen, onClose, account, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [profitAmount, setProfitAmount] = useState('');
  const [cryptocurrency, setCryptocurrency] = useState('BTC');
  const [walletAddress, setWalletAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const cryptoOptions = ['BTC', 'ETH', 'USDT', 'USDC', 'LTC', 'SOL', 'BNB', 'XRP', 'DOGE', 'TRX'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!amount || !profitAmount || !walletAddress) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/user/payout-request`, {
        account_id: account.id,
        amount: parseFloat(amount),
        profit_amount: parseFloat(profitAmount),
        cryptocurrency: cryptocurrency,
        wallet_address: walletAddress
      });

      // Verify the request was actually created
      if (response.data && response.data.id) {
        onSuccess?.();
        onClose();
        alert('Payout request submitted successfully! Request ID: ' + response.data.id);
      } else {
        setError('Request may not have been saved. Please refresh and check.');
      }
    } catch (err) {
      console.error('Payout request error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to submit payout request';
      setError(errorMessage);
      alert('Error: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)'
        }}
        onClick={onClose}
      />

      <div style={{
        position: 'relative',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-primary)',
        borderRadius: '24px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px',
          borderBottom: '1px solid var(--border-subtle)'
        }}>
          <div>
            <h2 style={{
              fontSize: '20px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              marginBottom: '4px'
            }}>
              Request Payout
            </h2>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-muted)'
            }}>
              ${account?.account_size?.toLocaleString()} Account
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              marginBottom: '24px'
            }}>
              <AlertCircle size={20} color="var(--error)" />
              <p style={{ color: 'var(--error)', fontSize: '14px' }}>{error}</p>
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>
              Profit Amount (Total Profit)
            </label>
            <input
              type="number"
              value={profitAmount}
              onChange={(e) => setProfitAmount(e.target.value)}
              placeholder="Enter total profit amount"
              step="0.01"
              min="0"
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-primary)',
                borderRadius: '10px',
                color: 'var(--text-primary)',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>
              Withdrawal Amount (USD)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount to withdraw"
              step="0.01"
              min="0"
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-primary)',
                borderRadius: '10px',
                color: 'var(--text-primary)',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>
              Cryptocurrency
            </label>
            <select
              value={cryptocurrency}
              onChange={(e) => setCryptocurrency(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-primary)',
                borderRadius: '10px',
                color: 'var(--text-primary)',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              {cryptoOptions.map(crypto => (
                <option key={crypto} value={crypto}>{crypto}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>
              Wallet Address ({cryptocurrency})
            </label>
            <input
              type="text"
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              placeholder={`Enter your ${cryptocurrency} wallet address`}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-primary)',
                borderRadius: '10px',
                color: 'var(--text-primary)',
                fontSize: '14px',
                fontFamily: 'monospace'
              }}
            />
          </div>

          <div style={{
            background: 'rgba(14, 165, 233, 0.1)',
            border: '1px solid rgba(14, 165, 233, 0.2)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <p style={{
              fontSize: '13px',
              color: 'var(--text-secondary)',
              lineHeight: 1.6
            }}>
              <strong style={{ color: 'var(--accent-blue)' }}>Note:</strong> Your payout request will be reviewed by admin. You will receive your funds in {cryptocurrency} to the provided wallet address upon approval.
            </p>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="btn-primary"
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px'
            }}
          >
            {loading ? 'Submitting...' : 'Submit Payout Request'}
          </Button>
        </form>
      </div>
    </div>
  );
};

// Funded Phase Request Modal
const FundedPhaseModal = ({ isOpen, onClose, account, onSuccess }) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/user/funded-phase-request`, {
        account_id: account.id,
        notes: notes
      });

      onSuccess?.();
      onClose();
      alert('Funded phase request submitted successfully! Admin will review shortly.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit funded phase request');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)'
        }}
        onClick={onClose}
      />

      <div style={{
        position: 'relative',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-primary)',
        borderRadius: '24px',
        maxWidth: '500px',
        width: '100%'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px',
          borderBottom: '1px solid var(--border-subtle)'
        }}>
          <div>
            <h2 style={{
              fontSize: '20px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              marginBottom: '4px'
            }}>
              Request Funded Phase
            </h2>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-muted)'
            }}>
              {account?.plan_name} - ${account?.account_size?.toLocaleString()}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              marginBottom: '24px'
            }}>
              <AlertCircle size={20} color="var(--error)" />
              <p style={{ color: 'var(--error)', fontSize: '14px' }}>{error}</p>
            </div>
          )}

          <div style={{
            background: 'var(--bg-primary)',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <h4 style={{
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--accent-gold)',
              marginBottom: '12px'
            }}>
              🎯 Evaluation Complete?
            </h4>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              lineHeight: 1.6
            }}>
              By submitting this request, you confirm that you have reached your profit target and are ready to move to the funded phase.
            </p>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>
              Additional Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional information for the admin..."
              rows={3}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-primary)',
                borderRadius: '10px',
                color: 'var(--text-primary)',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="btn-primary"
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px'
            }}
          >
            {loading ? 'Submitting...' : 'Request Funded Phase'}
          </Button>
        </form>
      </div>
    </div>
  );
};

const UserDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('accounts');
  const [showPasswords, setShowPasswords] = useState({});
  const [copiedField, setCopiedField] = useState(null);
  
  // Modal states
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showFundedPhaseModal, setShowFundedPhaseModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [accountsRes, payoutsRes, purchasesRes] = await Promise.all([
        axios.get(`${API_URL}/user/accounts`),
        axios.get(`${API_URL}/user/payout-requests`),
        axios.get(`${API_URL}/user/purchases`)
      ]);
      
      setAccounts(accountsRes.data);
      setPayoutRequests(payoutsRes.data);
      setPurchases(purchasesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const togglePassword = (accountId) => {
    setShowPasswords(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleRequestPayout = (account) => {
    setSelectedAccount(account);
    setShowPayoutModal(true);
  };

  const handleRequestFundedPhase = (account) => {
    setSelectedAccount(account);
    setShowFundedPhaseModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'funded':
      case 'approved':
      case 'paid':
      case 'passed':
      case 'finished':
      case 'confirmed':
        return 'var(--success)';
      case 'pending':
      case 'waiting':
      case 'confirming':
      case 'sending':
        return 'var(--warning)';
      case 'breached':
      case 'declined':
      case 'failed':
      case 'expired':
      case 'refunded':
        return 'var(--error)';
      default:
        return 'var(--text-muted)';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
      case 'funded':
      case 'approved':
      case 'paid':
      case 'passed':
      case 'finished':
      case 'confirmed':
        return <CheckCircle2 size={18} />;
      case 'pending':
      case 'waiting':
      case 'confirming':
      case 'sending':
        return <Clock size={18} />;
      case 'breached':
      case 'declined':
      case 'failed':
      case 'expired':
      case 'refunded':
        return <XCircle size={18} />;
      default:
        return <AlertCircle size={18} />;
    }
  };

  const canRequestFundedPhase = (account) => {
    return account.account_type === 'onestep' && 
           account.phase === 'evaluation' && 
           ['active', 'passed'].includes(account.status) &&
           account.funded_phase_request_status !== 'pending';
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)'
    }}>
      {/* Header */}
      <header style={{
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-subtle)',
        padding: '16px 24px'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <h1 style={{
              fontSize: '24px',
              fontWeight: 700,
              color: 'var(--accent-gold)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }} onClick={() => navigate('/')}>
              <img src="/logo.png" alt="Prop Indexa" style={{ height: '35px' }} />
              PROP INDEXA
            </h1>
            <div style={{
              padding: '4px 12px',
              background: 'rgba(212, 175, 55, 0.1)',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--accent-gold)',
              textTransform: 'uppercase'
            }}>
              Trader
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{ textAlign: 'right' }} className="hidden md:block">
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--text-primary)'
              }}>
                {user?.full_name}
              </div>
              <div style={{
                fontSize: '12px',
                color: 'var(--text-muted)'
              }}>
                {user?.email}
              </div>
            </div>

            <Button
              onClick={handleLogout}
              variant="ghost"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: 'var(--text-muted)'
              }}
            >
              <LogOut size={18} />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '32px 24px'
      }}>
        {/* Welcome Banner */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(14, 165, 233, 0.1) 100%)',
          border: '1px solid var(--border-primary)',
          borderRadius: '20px',
          padding: '32px',
          marginBottom: '32px'
        }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: 700,
            color: 'var(--text-primary)',
            marginBottom: '8px'
          }}>
            Welcome back, {user?.full_name?.split(' ')[0]}! 👋
          </h2>
          <p style={{
            fontSize: '16px',
            color: 'var(--text-secondary)'
          }}>
            Manage your trading accounts, request payouts, and track your performance.
          </p>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '32px',
          borderBottom: '1px solid var(--border-subtle)',
          overflowX: 'auto'
        }}>
          {[
            { id: 'accounts', label: 'My Accounts', icon: <BarChart3 size={18} /> },
            { id: 'achievements', label: 'Achievements', icon: <Award size={18} /> },
            { id: 'affiliate', label: 'Affiliate', icon: <Users size={18} /> },
            { id: 'payouts', label: 'Payout Requests', icon: <Wallet size={18} /> },
            { id: 'purchases', label: 'Purchase History', icon: <DollarSign size={18} /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid var(--accent-gold)' : '2px solid transparent',
                color: activeTab === tab.id ? 'var(--accent-gold)' : 'var(--text-muted)',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '64px 24px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid var(--border-primary)',
              borderTop: '4px solid var(--accent-gold)',
              borderRadius: '50%',
              margin: '0 auto 16px',
              animation: 'spin 1s linear infinite'
            }} />
            <p style={{ color: 'var(--text-muted)' }}>Loading your data...</p>
          </div>
        ) : (
          <>
            {/* My Accounts Tab */}
            {activeTab === 'accounts' && (
              <div>
                {accounts.length === 0 ? (
                  <div style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '20px',
                    padding: '64px 32px',
                    textAlign: 'center'
                  }}>
                    <BarChart3 size={48} style={{
                      color: 'var(--text-muted)',
                      margin: '0 auto 16px'
                    }} />
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      marginBottom: '8px'
                    }}>
                      No Trading Accounts Yet
                    </h3>
                    <p style={{
                      color: 'var(--text-muted)',
                      marginBottom: '24px'
                    }}>
                      Purchase a plan to get started with your funded trading account
                    </p>
                    <Button
                      onClick={() => navigate('/')}
                      className="btn-primary"
                    >
                      View Plans
                    </Button>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
                    gap: '24px'
                  }}>
                    {accounts.map(account => (
                      <div
                        key={account.id}
                        style={{
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: '20px',
                          padding: '24px',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {/* Account Header */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '16px'
                        }}>
                          <h3 style={{
                            fontSize: '24px',
                            fontWeight: 700,
                            color: 'var(--accent-gold)'
                          }}>
                            ${account.account_size?.toLocaleString()}
                          </h3>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            background: `${getStatusColor(account.status)}20`,
                            borderRadius: '20px',
                            color: getStatusColor(account.status),
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                          }}>
                            {getStatusIcon(account.status)}
                            <span>{account.status}</span>
                          </div>
                        </div>

                        {/* PnL Display */}
                        <div data-testid={`user-pnl-${account.id}`} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          background: (account.pnl || 0) >= 0
                            ? 'rgba(16, 185, 129, 0.08)'
                            : 'rgba(239, 68, 68, 0.08)',
                          border: `1px solid ${(account.pnl || 0) >= 0 ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
                          borderRadius: '12px',
                          marginBottom: '16px'
                        }}>
                          <span style={{
                            fontSize: '13px',
                            color: 'var(--text-muted)',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                          }}>
                            Total PnL
                          </span>
                          <span style={{
                            fontSize: '20px',
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            color: (account.pnl || 0) >= 0 ? 'var(--success)' : 'var(--error)'
                          }}>
                            {(account.pnl || 0) >= 0 ? '+' : ''}${(account.pnl || 0).toFixed(2)}
                          </span>
                        </div>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          color: 'var(--text-muted)',
                          marginBottom: '16px'
                        }}>
                          <span>{account.plan_name}</span>
                          <span>•</span>
                          <span>{account.platform}</span>
                          {account.phase && (
                            <>
                              <span>•</span>
                              <span style={{
                                padding: '2px 8px',
                                background: account.phase === 'funded' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(14, 165, 233, 0.1)',
                                borderRadius: '4px',
                                color: account.phase === 'funded' ? 'var(--success)' : 'var(--accent-blue)',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                fontSize: '11px'
                              }}>
                                {account.phase}
                              </span>
                            </>
                          )}
                        </div>

                        {/* Credentials Section */}
                        <div style={{
                          background: 'var(--bg-primary)',
                          borderRadius: '12px',
                          padding: '16px',
                          marginBottom: '16px'
                        }}>
                          <h4 style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            marginBottom: '12px',
                            textTransform: 'uppercase'
                          }}>
                            MT5 Login Credentials
                          </h4>
                          
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}>
                              <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Login ID</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                                  {account.login_id}
                                </span>
                                <button
                                  onClick={() => copyToClipboard(account.login_id, `login-${account.id}`)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: copiedField === `login-${account.id}` ? 'var(--success)' : 'var(--text-muted)',
                                    padding: '4px'
                                  }}
                                >
                                  {copiedField === `login-${account.id}` ? <Check size={14} /> : <Copy size={14} />}
                                </button>
                              </div>
                            </div>
                          </div>

                          <div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}>
                              <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Password</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                                  {showPasswords[account.id] ? account.password : '••••••••'}
                                </span>
                                <button
                                  onClick={() => togglePassword(account.id)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--text-muted)',
                                    padding: '4px'
                                  }}
                                >
                                  {showPasswords[account.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                                <button
                                  onClick={() => copyToClipboard(account.password, `pass-${account.id}`)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: copiedField === `pass-${account.id}` ? 'var(--success)' : 'var(--text-muted)',
                                    padding: '4px'
                                  }}
                                >
                                  {copiedField === `pass-${account.id}` ? <Check size={14} /> : <Copy size={14} />}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Account Details */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '12px',
                          padding: '16px',
                          background: 'var(--bg-primary)',
                          borderRadius: '12px',
                          fontSize: '13px',
                          marginBottom: '16px'
                        }}>
                          <div>
                            <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>
                              Daily DD
                            </div>
                            <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                              {account.daily_drawdown_percent}% (${account.daily_drawdown_amount})
                            </div>
                          </div>
                          <div>
                            <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>
                              Max DD
                            </div>
                            <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                              {account.max_drawdown_percent}% (${account.max_drawdown_amount})
                            </div>
                          </div>
                          <div>
                            <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>
                              Profit Split
                            </div>
                            <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                              {account.profit_split}%
                            </div>
                          </div>
                          {account.profit_target && (
                            <div>
                              <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>
                                Profit Target
                              </div>
                              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                {account.profit_target}%
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Funded Phase Request Status */}
                        {account.funded_phase_request_status && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px',
                            background: `${getStatusColor(account.funded_phase_request_status)}10`,
                            borderRadius: '8px',
                            marginBottom: '16px'
                          }}>
                            {getStatusIcon(account.funded_phase_request_status)}
                            <span style={{
                              fontSize: '13px',
                              color: getStatusColor(account.funded_phase_request_status)
                            }}>
                              Funded Phase Request: {account.funded_phase_request_status.toUpperCase()}
                            </span>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: '12px' }}>
                          {/* Request Funded Phase Button (for ONESTEP in evaluation) */}
                          {canRequestFundedPhase(account) && (
                            <Button
                              onClick={() => handleRequestFundedPhase(account)}
                              style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                background: 'rgba(14, 165, 233, 0.1)',
                                border: '1px solid rgba(14, 165, 233, 0.3)',
                                color: 'var(--accent-blue)'
                              }}
                            >
                              <ArrowUpRight size={18} />
                              Request Funded Phase
                            </Button>
                          )}

                          {/* Request Payout Button (for FUNDED accounts) */}
                          {account.status === 'funded' && (
                            <Button
                              onClick={() => handleRequestPayout(account)}
                              className="btn-primary"
                              style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                              }}
                            >
                              <Wallet size={18} />
                              Request Payout
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Achievements Tab */}
            {activeTab === 'achievements' && (
              <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: '20px',
                padding: '24px',
                border: '1px solid var(--border-subtle)'
              }}>
                <AchievementsSection />
              </div>
            )}

            {/* Affiliate Tab */}
            {activeTab === 'affiliate' && (
              <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: '20px',
                padding: '24px',
                border: '1px solid var(--border-subtle)'
              }}>
                <AffiliateSection />
              </div>
            )}

            {/* Payout Requests Tab */}
            {activeTab === 'payouts' && (
              <div>
                {payoutRequests.length === 0 ? (
                  <div style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '20px',
                    padding: '64px 32px',
                    textAlign: 'center'
                  }}>
                    <Wallet size={48} style={{
                      color: 'var(--text-muted)',
                      margin: '0 auto 16px'
                    }} />
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      marginBottom: '8px'
                    }}>
                      No Payout Requests Yet
                    </h3>
                    <p style={{
                      color: 'var(--text-muted)'
                    }}>
                      Once your account is funded, you can request payouts here
                    </p>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gap: '16px'
                  }}>
                    {payoutRequests.map(request => (
                      <div
                        key={request.id}
                        style={{
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: '16px',
                          padding: '24px'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '16px',
                          flexWrap: 'wrap',
                          gap: '16px'
                        }}>
                          <div>
                            <div style={{
                              fontSize: '24px',
                              fontWeight: 700,
                              color: 'var(--accent-gold)',
                              marginBottom: '4px'
                            }}>
                              ${request.amount?.toLocaleString()}
                            </div>
                            <div style={{
                              fontSize: '14px',
                              color: 'var(--text-muted)'
                            }}>
                              From ${request.profit_amount?.toLocaleString()} profit
                            </div>
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            background: `${getStatusColor(request.status)}20`,
                            borderRadius: '20px',
                            color: getStatusColor(request.status),
                            fontSize: '13px',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                          }}>
                            {getStatusIcon(request.status)}
                            <span>{request.status}</span>
                          </div>
                        </div>

                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '16px',
                          padding: '16px',
                          background: 'var(--bg-primary)',
                          borderRadius: '12px',
                          fontSize: '14px'
                        }}>
                          <div>
                            <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>
                              Cryptocurrency
                            </div>
                            <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                              {request.cryptocurrency}
                            </div>
                          </div>
                          <div>
                            <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>
                              Wallet Address
                            </div>
                            <div style={{ 
                              color: 'var(--text-primary)', 
                              fontWeight: 600,
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              wordBreak: 'break-all'
                            }}>
                              {request.wallet_address}
                            </div>
                          </div>
                          <div>
                            <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>
                              Requested
                            </div>
                            <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                              {request.requested_at ? new Date(request.requested_at).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>
                        </div>

                        {request.admin_notes && (
                          <div style={{
                            marginTop: '12px',
                            padding: '12px',
                            background: 'rgba(14, 165, 233, 0.05)',
                            borderRadius: '8px',
                            fontSize: '13px',
                            color: 'var(--text-secondary)'
                          }}>
                            <strong style={{ color: 'var(--accent-blue)' }}>Admin Note:</strong> {request.admin_notes}
                          </div>
                        )}

                        {request.decline_reason && (
                          <div style={{
                            marginTop: '12px',
                            padding: '12px',
                            background: 'rgba(239, 68, 68, 0.05)',
                            borderRadius: '8px',
                            fontSize: '13px',
                            color: 'var(--text-secondary)'
                          }}>
                            <strong style={{ color: 'var(--error)' }}>Declined:</strong> {request.decline_reason}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Purchases Tab */}
            {activeTab === 'purchases' && (
              <div>
                {purchases.length === 0 ? (
                  <div style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '20px',
                    padding: '64px 32px',
                    textAlign: 'center'
                  }}>
                    <DollarSign size={48} style={{
                      color: 'var(--text-muted)',
                      margin: '0 auto 16px'
                    }} />
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      marginBottom: '8px'
                    }}>
                      No Purchases Yet
                    </h3>
                    <p style={{
                      color: 'var(--text-muted)',
                      marginBottom: '24px'
                    }}>
                      Purchase a trading plan to get started
                    </p>
                    <Button
                      onClick={() => navigate('/')}
                      className="btn-primary"
                    >
                      Browse Plans
                    </Button>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gap: '16px'
                  }}>
                    {purchases.map(purchase => (
                      <div
                        key={purchase.id}
                        style={{
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: '16px',
                          padding: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: '16px'
                        }}
                      >
                        <div>
                          <div style={{
                            fontSize: '20px',
                            fontWeight: 700,
                            color: 'var(--text-primary)',
                            marginBottom: '4px'
                          }}>
                            {purchase.plan_type?.toUpperCase()} ${purchase.account_size?.toLocaleString()}
                          </div>
                          <div style={{
                            fontSize: '14px',
                            color: 'var(--text-muted)'
                          }}>
                            ${purchase.amount_paid} • {purchase.purchased_at ? new Date(purchase.purchased_at).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            background: `${getStatusColor(purchase.payment_status)}20`,
                            borderRadius: '20px',
                            color: getStatusColor(purchase.payment_status),
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                          }}>
                            {getStatusIcon(purchase.payment_status)}
                            <span>{purchase.payment_status}</span>
                          </div>

                          {purchase.account_assigned ? (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              background: 'rgba(16, 185, 129, 0.1)',
                              borderRadius: '20px',
                              color: 'var(--success)',
                              fontSize: '12px',
                              fontWeight: 600
                            }}>
                              <CheckCircle2 size={14} />
                              <span>Account Assigned</span>
                            </div>
                          ) : (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              background: 'rgba(245, 158, 11, 0.1)',
                              borderRadius: '20px',
                              color: 'var(--warning)',
                              fontSize: '12px',
                              fontWeight: 600
                            }}>
                              <Clock size={14} />
                              <span>Pending Assignment</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <PayoutRequestModal
        isOpen={showPayoutModal}
        onClose={() => setShowPayoutModal(false)}
        account={selectedAccount}
        onSuccess={fetchData}
      />

      <FundedPhaseModal
        isOpen={showFundedPhaseModal}
        onClose={() => setShowFundedPhaseModal(false)}
        account={selectedAccount}
        onSuccess={fetchData}
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default UserDashboard;
